// React and Hooks
import React, { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'

// Icons
import { MdOutlineDelete } from 'react-icons/md'
import { CiEdit } from 'react-icons/ci'

// Components
import Button from '../Custom/Buttons/Button'
import CustomToast from '../Custom/CustomToast'

// Utils
import axiosInterceptorInstance from '../../utils/axios/axiosInterceptorInstance'
import { useDebounce } from '../../hooks/useDebounce'
import configManager from '../../config/configManager'

// Assets
import brokenImg from '../../assets/images/brokenImg.png'

const Brands = ({ userPermissions }) => {
    const configData = configManager()
    const [loading, setLoading] = useState(false)

    const [page, setPage] = useState(1)
    const [limit] = useState(50)
    const [search, setSearch] = useState('')

    const [brands, setBrands] = useState([])
    const [moreAvailable, setMoreAvailable] = useState(true)

    const debouncedSearchTerm = useDebounce(search, 300)

    const [deleteBrands, setDeleteBrand] = useState([])
    const [isDeleting, setIsDeleting] = useState(false)

    // Wrap fetchBrands in useCallback to memoize it
    const fetchBrands = useCallback(async (params) => {
        const { page = 1, limit = 50, search = null } = params

        const urlParams = new URLSearchParams({
            page,
            limit,
            ...(search && { search }),
        })

        setLoading(true)
        try {
            const { data } = await axiosInterceptorInstance.post(
                `/brands?${urlParams.toString()}`,
                {
                    body: JSON.stringify(params),
                },
            )

            if (data?.success) {
                setBrands((prev) =>
                    page === 1 ? data.data.results : [...prev, ...data.data.results],
                )
                setMoreAvailable(data.data?.pagination?.hasNext || false)
            }
        } catch (error) {
            CustomToast({
                message: 'Failed to fetch brands. Please try again!',
                type: 'error',
            })
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        setPage(1)
        setBrands([])
        fetchBrands({
            page: 1,
            limit,
            search: debouncedSearchTerm,
        })
    }, [debouncedSearchTerm, limit, fetchBrands]) // Added missing dependencies

    const handleLoadMore = () => {
        const nextPage = page + 1
        fetchBrands({
            page: nextPage,
            limit,
            search: debouncedSearchTerm,
        })
        setPage(nextPage)
    }

    const selectForDelete = (brandId) => {
        if (!userPermissions.includes('brand:delete')) return

        if (deleteBrands.includes(brandId)) {
            setDeleteBrand((pre) => pre.filter((val) => val !== brandId))
        } else {
            setDeleteBrand((pre) => [...pre, brandId])
        }
    }

    const selectAllForDelete = () => {
        if (!userPermissions.includes('brand:delete')) return

        if (deleteBrands.length === brands.length) {
            setDeleteBrand([])
        } else {
            setDeleteBrand(brands.map((brand) => brand._id))
        }
    }

    const deleteBrandsHandler = async () => {
        if (!userPermissions.includes('brand:delete')) return
        setIsDeleting(true)

        if (!deleteBrands.length) {
            CustomToast({
                message: 'Select brands to delete.',
                type: 'warning',
            })
            setIsDeleting(false)
            return
        }

        try {
            const response = await axiosInterceptorInstance.delete(`/brands/delete-multiple`, {
                data: { ids: deleteBrands },
            })
            if (response.data.success) {
                setBrands((pre) => {
                    return pre.filter((brand) => {
                        return !deleteBrands.some((id) => id === brand._id)
                    })
                })
                CustomToast({
                    message: 'Brands deleted successfully.',
                    type: 'success',
                })
            }
        } catch (error) {
            CustomToast({
                message: error.response?.data?.message || 'Failed to delete brands!',
                type: 'error',
            })
        } finally {
            setIsDeleting(false)
            setDeleteBrand([])
        }
    }

    return (
        <div>
            <div className='flex gap-4 justify-between items-center flex-wrap'>
                {deleteBrands.length && userPermissions.includes('brand:delete') ? (
                    <Button
                        label='Delete'
                        disabled={isDeleting}
                        isLoading={isDeleting}
                        onClick={deleteBrandsHandler}
                        className='flex gap-1.5 items-center font-medium rounded-lg disabled:pointer-events-none disabled:opacity-70 border-2 !border-red-700 hover:!bg-red-700 !text-red-700 hover:!text-white'
                    >
                        <MdOutlineDelete className='text-2xl' />
                    </Button>
                ) : null}

                {deleteBrands.length && userPermissions.includes('brand:delete') ? (
                    <label className='flex gap-2 items-center font-semibold'>
                        <input
                            className='w-5 h-5'
                            type='checkbox'
                            checked={deleteBrands.length === brands.length}
                            onChange={selectAllForDelete}
                        />
                        Select All
                    </label>
                ) : null}

                <input
                    placeholder='Search brands...'
                    className='w-full sm:w-[300px] ml-auto border-[2px] border-prime rounded-lg p-3 focus:outline-none focus:border-orange'
                    type='text'
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />
            </div>

            {loading ? <p className='text-center my-20'>Loading...</p> : ''}

            {!loading && brands.length === 0 ? (
                <p className='text-center my-10'>No brands available.</p>
            ) : (
                ''
            )}

            <div className='grid grid-cols-2 sm:grid-cols-4 md:grid-cols-5 gap-4 my-8'>
                {brands.map((brand) => (
                    <div key={brand._id} className='rounded'>
                        <div className='rounded-xl relative'>
                            <div className='relative group'>
                                {userPermissions.includes('brand:update') ? (
                                    <Link
                                        to={`/brand/update/${brand._id}`}
                                        className='absolute bottom-2 right-2 bg-prime p-2 text-xl hidden text-white rounded-full group-hover:inline-block'
                                    >
                                        <CiEdit />
                                    </Link>
                                ) : null}
                                <img
                                    className={`w-full aspect-square object-contain rounded-xl p-2 ${deleteBrands?.includes(brand._id)
                                            ? 'border-4 border-prime'
                                            : ''
                                        }`}
                                    src={
                                        brand.logo
                                            ? `${configData.CDN_BASE_URL}/${brand.logo.startsWith('/')
                                                ? brand.logo.slice(1)
                                                : 'brands/' + brand.logo
                                            }`
                                            : brokenImg
                                    }
                                    onError={(e) => (e.target.src = brokenImg)}
                                    alt={brand.name || 'Brand Logo'}
                                    onClick={() =>
                                        userPermissions.includes('brand:delete')
                                            ? selectForDelete(brand._id)
                                            : null
                                    }
                                />
                                {userPermissions.includes('brand:delete') ? (
                                    <input
                                        type='checkbox'
                                        className={`w-4 h-4 absolute top-4 right-4 ${deleteBrands?.includes(brand._id) ? 'block' : 'hidden'
                                            }`}
                                        checked={deleteBrands?.includes(brand._id)}
                                        onChange={() => selectForDelete(brand._id)}
                                    />
                                ) : null}
                            </div>
                        </div>
                        <div className='py-3 px-2'>
                            {brand.name && (
                                <div className='font-semibold text-center truncate'>{brand.name}</div>
                            )}
                            {/* {brand.country && (
                                <div className='text-gray-600 text-center text-sm'>{brand.country}</div>
                            )} */}
                        </div>
                    </div>
                ))}
            </div>

            {moreAvailable && brands.length ? (
                <Button
                    onClick={handleLoadMore}
                    disabled={loading}
                    isLoading={loading}
                    label='Explore More'
                    className='w-fit mx-auto rounded-full !px-8 border-2 border-prime hover:bg-prime hover:text-white flex gap-2 items-center justify-between transition-all'
                />
            ) : (
                ''
            )}
        </div>
    )
}

export default Brands